import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { messageBundle, storeNode, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';
import { VNode } from 'src/interfaces/interfaces';
import PublishAppViewModel from '../data/PublishApp/PublishAppViewModel';
import { keyboardAccessibleHelper } from '../utils/appUtils';

import { EAppTemplateType, } from 'templates-common-library/baseClasses/CompatibilityChecker';
import AppTypeInfoModal from './AppTypeInfoModal';
import AppTypeTileViewModel from './AppTypeTile/AppTypeTileViewModel';

import "./AppTypeTile/styles/AppTypeTile.scss";
import IFrameAppPreview from './IFrameAppPreview';
import PublishForm from './PublishForm';

import CircleDownCarotSVG from "../../../public/assets/images/Info_icon.svg";
import { applyShadowStyle, applyTextFade } from '../utils/utils';
import InfoBadge from './InfoBadge';
import { ddButtonDef } from './AppTile';
import { focusElement } from '../utils/focusUtils';
import { SHADOW_STYLE_DROPDOWN } from '../utils/shadowStyles';
import ContentAreaViewModel from '../ui/ContentArea/ContentAreaViewModel';

const INTRINSIC_IMG_SIZE = 1000;

export interface AppTypeInfo {
  thumbnailUrl: string;
  screenshotUrl: string;
  created?: Date;
  badge?: string;
  appTypeTitle: string;
  appTypeUrlFragment: EAppTemplateType; // end url (/apps/instant/3dviewer/index.html)
  appTypeUrl: string; // full url for portal (ex: https://devext.arcgis.com/apps/instant/3dviewer/index.html)
  compatibleMsgs: string | null;
  appTypeId: string; // Id of the App Type's PortalItem
  portalItem: __esri.PortalItem
}

export interface ResourceInfo {
  resourceItem: resourceItem;
  resourceType: EResourceType;
}

export interface AppTypeTileProperties extends __esri.WidgetProperties {
  uniqueTileId: string;
  appTypeInfo: AppTypeInfo;
  publishAppVM: PublishAppViewModel;
  resourceInfo: ResourceInfo;
  contentAreaVM: ContentAreaViewModel;
}

export enum EResourceType {
  Webmap,
  Webscene,
  Group,
  None,

  Unknown
}
export type resourceItem = __esri.PortalItem | __esri.PortalGroup;
export type previewType = "samplePreview" | "mapPreview";


@subclass('Landing.widgets.AppTypeTile')
export default class AppTypeTile extends Widget {
  @property()
  identifier: "AppTypeTile" = "AppTypeTile";

  @property()
  uniqueTileId: string;

  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  resourceInfo: ResourceInfo;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  publishModal: PublishForm;

  @property()
  infoModal: AppTypeInfoModal;

  @property()
  iFramePreviewModal: IFrameAppPreview;

  @property()
  previewButtonDOM: HTMLCalciteButtonElement;

  @property()
  @messageBundle("dist/assets/t9n/widgets/AppTypeTile/AppTypeTile")
  messages;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  viewModel = new AppTypeTileViewModel();

  @property()
  infoBadge: InfoBadge;

  private _previewType: previewType;
  private _hamburgerMenuButtons: ddButtonDef[];

  /** Saved active element when a modal is launched */
  private _activeElement: HTMLElement;

  constructor(properties?: AppTypeTileProperties) {
    super(properties);
  }

  postInitialize() {
    const { badge, thumbnailUrl, appTypeUrlFragment } = this.appTypeInfo;
    this._previewType = this._decidePreviewBtnTitle();

    this.appTypeInfo.thumbnailUrl = this._decideThumbnailUrl(thumbnailUrl);
    if (badge && !this.publishAppVM.portal.isPortal) {
      this.infoBadge = new InfoBadge({ badge, appType: appTypeUrlFragment, uniqueId: `badge${this.uniqueTileId}` });
    }

    this._initHamburgerMenu();
  }

  render() {
    const tileImg = this._renderTileImg();
    const titleArea = this._renderTitleArea();
    const buttonArea = this._renderButtonArea();
    const { compatibleMsgs } = this?.appTypeInfo;
    return (
      <div class="app-type-tile">
        { tileImg}
        { titleArea}
        { buttonArea}
        { compatibleMsgs != null ? (
          <div id="notcompatMsgs" key="notcompatMsgs">
            <calcite-icon
              id="warning-icon"
              icon="exclamation-mark-triangle-f"
              scale="s">
            </calcite-icon>
            <div key={`notcompatMsg${this.appTypeInfo.appTypeId}`} class="warning-text">
              {compatibleMsgs}
            </div>
          </div>) : null}

        { this.publishModal?.render()}
        { this.infoModal?.render()}
        { this.iFramePreviewModal?.render()}

      </div>
    );
  }

  private _initHamburgerMenu() {
    const { viewItemDetails, appDescription } = this.appMessages.hamburgerBtns;
    this._hamburgerMenuButtons = [
      {
        icon: "infographic",
        label: viewItemDetails,
        onClickFunc: () => {
          window.open(`${this.publishAppVM.orgUrl}/home/item.html?id=${this.appTypeInfo.appTypeId}`);
        }
      },
      {
        icon: "information",
        label: appDescription,
        onClickFunc: () => {
          this._initializeInfoModal();
        }
      }
    ]

    if (!this._isPortal() && this._isAppStudioSupportedTemplate()) {
      this._hamburgerMenuButtons.push(
        {
          icon: "app-run",
          label: "ArcGIS AppStudio", // Hardcoded because it's a product name
          onClickFunc: () => {
            switch (this.appTypeInfo.appTypeUrlFragment) {
              case EAppTemplateType.AttachmentViewer:
                window.open("https://doc.arcgis.com/en/appstudio/configure-apps/templateattachmentviewer.htm");
                break;
              case EAppTemplateType.Nearby:
                window.open("https://doc.arcgis.com/en/appstudio/configure-apps/templatenearby.htm");
                break;
              case EAppTemplateType.ZoneLookup:
                window.open("https://doc.arcgis.com/en/appstudio/configure-apps/templatezonelookup.htm");
                break;
            }
          }
        }
      )
    }
  }

  private _renderTitleArea() {
    const { appTypeTitle } = this?.appTypeInfo;
    return (
      <div class="app-type-tile__title">
        <div class="leftAlign">
          {this._renderHamburgerMenu()}
          <div id="app-title">
            <div>
              <span afterCreate={applyTextFade} title={appTypeTitle}>
                {appTypeTitle}
              </span>
            </div>
          </div>
          <img
            src={CircleDownCarotSVG}
            tabindex="0"
            onclick={this._initializeInfoModal.bind(this)}
            onkeydown={keyboardAccessibleHelper.bind(
              this,
              this._initializeInfoModal.bind(this)
            )}
            class="down-chevron-svg"
            alt="" />
        </div>
        {this.infoBadge?.render()}
      </div>
    )
  }

  private _renderButtonArea() {
    const isSampleAndInPortal: boolean = this._checkIfSampleAndPortal();
    return (
      <div class="app-type-tile__buttons">
        { isSampleAndInPortal ? null : <calcite-button
          bind={this}
          afterCreate={storeNode}
          data-node-ref="previewButtonDOM"
          id="previewBtn"
          class="previewBtn"
          onclick={this._initializePreview.bind(this)}
          width="full"
          scale="m"
          icon-start={this._previewType === "mapPreview" ? "camera-flash-on" : "analysis"}
          appearance="outline"
          title={this.appMessages.previewButtons[this._previewType]}>
          {this.appMessages.previewButtons[this._previewType]}
        </calcite-button>}
        <calcite-button
          id="chooseBtn"
          class={this.classes("chooseBtn", isSampleAndInPortal && "chooseBtn--only")}
          onclick={this._initializePublish.bind(this)}
          width="full"
          scale="m"
        >
          {this.messages.chooseBtn}
        </calcite-button>
      </div>)
  }

  /** Checks if compat msg exists and the portal is disconnected. If true then the Preview button gets hidden */
  private _checkIfSampleAndPortal(): boolean {
    return this._previewType === "samplePreview" && this.publishAppVM.portal.isPortal;
  }

  private _renderTileImg(): VNode {
    const {
      thumbnailUrl,
      screenshotUrl
    } = this.appTypeInfo;

    return (
      <img class="app-type-tile__img"
        src={screenshotUrl != null ? screenshotUrl : thumbnailUrl} />
    );
  }

  private _initializePublish() {
    this._saveActiveElem();
    const { appTypeInfo, resourceInfo } = this;
    this.publishModal = new PublishForm({
      ui: "modal",
      mode: "newApp",
      appTypeInfo,
      resourceInfo,
      publishAppVM: this.publishAppVM,
      feedbackFunction: (msg: string, success: boolean, appId?: string) => {
        this.contentAreaVM.myAppsDataVM.queryAllMyApps();
      },
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializeInfoModal() {
    this._saveActiveElem();
    const { thumbnailUrl, appTypeTitle, appTypeUrlFragment } = this.appTypeInfo;
    this.infoModal = new AppTypeInfoModal({
      picUrl: thumbnailUrl,
      appTypeTitle: appTypeTitle,
      appTypeUrlFragment: appTypeUrlFragment,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _initializePreview(): void {
    this._saveActiveElem();
    const { appTypeInfo, resourceInfo, publishAppVM } = this;
    this.iFramePreviewModal = new IFrameAppPreview({
      appTypeInfo,
      resourceInfo,
      publishAppVM,
      previewType: this._previewType,
      sendFocusOnClose: this._focusPriorActiveElem()
    });
  }

  private _renderHamburgerMenu(): VNode {
    return (
      <calcite-dropdown
        class="app-type-tile__dropdown-options"
        placement="bottom-leading"
        type="click"
        scale="m"
        width="m"
      >
        <calcite-button
          slot="dropdown-trigger"
          scale="m"
          appearance="transparent"
          color="blue"
          icon-start="handleVertical"
          alignment="center"
          width="auto"
        ></calcite-button>
        <calcite-dropdown-group
          role="menu"
          selection-mode="none"
          selectionMode="none"
        >
          {this._renderDropDownOptions()}
        </calcite-dropdown-group>
      </calcite-dropdown>
    );
  }

  private _renderDropDownOptions(): VNode[] {
    return (
      this?._hamburgerMenuButtons?.map((option: ddButtonDef) => {
        const { icon, label, onClickFunc } = option;
        return (
          <calcite-dropdown-item
            afterCreate={(ddItem) => applyShadowStyle(SHADOW_STYLE_DROPDOWN, ddItem)}
            title={label}
            icon-start={icon}
            class="appTypeTileDropDownLinks"
            onclick={(e) => {
              e.preventDefault();
              onClickFunc.call(this, e);
            }}
          >
            {label}
          </calcite-dropdown-item>
        )
      })
    )
  }

  private _decideThumbnailUrl(defaultUrl: string): string {
    return defaultUrl.indexOf("?") !== -1
      ? `${defaultUrl}&w=${INTRINSIC_IMG_SIZE}`
      : `${defaultUrl}?w=${INTRINSIC_IMG_SIZE}`;
  }

  private _decidePreviewBtnTitle(): previewType {
    return this.resourceInfo.resourceItem != null && this.appTypeInfo.compatibleMsgs == null ? "mapPreview" : "samplePreview";
  }

  private _saveActiveElem() {
    this._activeElement = document.activeElement as HTMLElement;
  }

  private _focusPriorActiveElem(): Function {
    return focusElement.bind(this, this._activeElement);
  }

  private _isPortal(): boolean {
    return this?.publishAppVM?.portal?.isPortal;
  }

  private _isAppStudioSupportedTemplate(): boolean {
    return (
      this.appTypeInfo.appTypeUrlFragment === EAppTemplateType.AttachmentViewer
      || this.appTypeInfo.appTypeUrlFragment === EAppTemplateType.Nearby
      || this.appTypeInfo.appTypeUrlFragment === EAppTemplateType.ZoneLookup
    );
  }

}
